import { BlockJustText } from '@ddg-frontend/blocks'
import { useProgramSearch } from '../hooks/useProgramSearch'
import { Channel } from '../types'
import { useApp } from '../components/app-context'
import { useState, useEffect } from 'react'
import { ProgramDrawer } from '../components/program-drawer'
import { Link, useNavigate } from 'react-router-dom'
import { Input } from '@ddg-frontend/inputs'
import { ProgramCard } from '../components/program-card'
import { setMetaTag } from '../utils/setMetaTag'
import { getResource } from '../utils'
import recordings from '../assets/recordings.svg'
import { useIsInView } from '@ddg-frontend/fe-essentials'

export const ProgramSearchPage = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const searchTerm = urlParams.get('termo')
  const { data, isLoading } = useProgramSearch(searchTerm ?? '')
  const { isProgramRecording } = useApp()
  const [selectedInfo, setSelectedInfo] = useState<
    | {
        programId: number | undefined
        channel: Channel | undefined
        date: string | undefined
      }
    | undefined
  >(undefined)
  const onProgramClick = (
    id: number | undefined,
    channel: Channel | undefined,
    date: string | undefined = undefined
  ) => {
    if (id) {
      const url = new URL(window.location.href)
      url.searchParams.set('programId', String(id))
      url.searchParams.set('channel', channel?.callLetter || '')
      url.searchParams.set('date', date || '')
      window.history.pushState({}, '', url.toString())
    } else {
      const url = new URL(window.location.href)
      url.searchParams.delete('programId')
      url.searchParams.delete('channel')
      url.searchParams.delete('date')
      window.history.pushState({}, '', url.toString())
    }
    setSelectedInfo({
      programId: id,
      channel: channel,
      date: date,
    })
  }
  const { channelData } = useApp()
  const [searchValue, setSearchValue] = useState(searchTerm ?? '')
  const navigate = useNavigate()
  const [visibleCount, setVisibleCount] = useState(12)

  useEffect(() => {
    setVisibleCount(12)
  }, [data])
  useEffect(() => {
    document.title = `Guia TV`

    setMetaTag(
      'meta[name="description"]',
      'Encontre a programação dos canais de TV'
    )
    setMetaTag('meta[property="og:title"]', `Guia TV`)
    setMetaTag(
      'meta[property="og:description"]',
      'Encontre a programação dos canais de TV'
    )
  }, [])

  const [viewRef, isInView] = useIsInView<HTMLDivElement>({
    rootMargin: '0px',
    threshold: 0.01,
  })
  useEffect(() => {
    if (isInView && visibleCount < (data?.length || 0)) {
      setVisibleCount((prev) => Math.min(prev + 12, data?.length || 0))
    }
  }, [isInView])
  const isSapo = !!import.meta.env.VITE_IS_SAPO
  const language = window.ISites.WegLot?.GetCurrentLanguage() || 'PT'
  return (
    <div className="search-page">
      <BlockJustText
        content={{
          state: 'FULFILLED',
          data: [
            {
              id: '0',
              texts: {
                title: 'Resultados de Pesquisa',
              },
              hyperlinks: null,
              itemTextColor: null,
              labels: null,
              stamp: null,
              countdown: null,
            },
          ],
        }}
        configs={{
          attributes: {},
          blockBackgroundColor: '',
          blockBackgroundImage: '',
          blockLine: '',
          blockMargin: '',
          blockTextAlignment: 'txt-align-center',
          blockTextColor: '',
          itemBackgroundColor: '',
          itemTextColor: '',
          itemTextAlignment: 'txt-align-center',
          mode: '',
          loadingRatio: '1:1',
          initialSelected: '',
          blockAnchor: '',
          extraClasses: '',
          collapsable: false,
          fullWidth: '',
          imagePosition: '',
          colNumber: '3',
          activeItem: '',
          textAreaAlignment: '',
          verticalTextAlignment: '',
          eventTracking: false,
          eventTrackingCategory: '',
          siteContext: 'meo',
        }}
      />
      <div className="search-navigation">
        <Link
          to="/"
          className="btn-color-1 btn-text icon-seta-left btn-icon-left"
        >
          {getResource('TVGuide')}
        </Link>
        <div className="right-section">
          <Link
            to={`/${language === 'EN' ? 'recordings' : 'gravacoes-agendadas'}`}
            className="recording-icon"
            target={isSapo ? '_blank' : undefined}
          >
            <img src={recordings} alt="Gravações" />
            <span className="screen-readers-only">Ver as minhas gravações</span>
          </Link>
          <Input
            label="Pesquisar Programa"
            className="search-input"
            onChange={(e) => setSearchValue(e.currentTarget.value)}
            onClick={() => {
              if (searchValue) {
                navigate(
                  `/${
                    language === 'EN' ? 'search-results' : 'pesquisa'
                  }?termo=${searchValue}`
                )
              }
            }}
            value={searchValue}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                navigate(
                  `/${
                    language === 'EN' ? 'search-results' : 'pesquisa'
                  }?termo=${searchValue}`
                )
              }
            }}
          />
        </div>
      </div>
      {isLoading && <div className={'block-loading'} />}
      {!data?.length && !isLoading && (
        <div className="no-results-message">
          <div>
            <h6>{getResource('Search.NoResults.Title')}</h6>
            <p>{getResource('Search.NoResults.Description')}</p>
          </div>
        </div>
      )}
      <div className="search-container">
        {data?.slice(0, visibleCount).map((program) => (
          <ProgramCard
            key={program.id}
            program={program}
            recordingType={isProgramRecording(program.programId)}
            channel={channelData?.find(
              (channel) => channel.callLetter === program.callLetter
            )}
            onProgramClick={onProgramClick}
          />
        ))}
        {visibleCount < (data?.length || 0) && (
          <div className="load-more-indicator" ref={viewRef}>
            {getResource('Search.Loading')}
          </div>
        )}
      </div>
      <ProgramDrawer
        programId={selectedInfo?.programId}
        onProgramClick={onProgramClick}
        channel={selectedInfo?.channel}
        date={selectedInfo?.date}
      />
    </div>
  )
}
